import { Building2, CheckCircle2, WifiOff, Clock, AlertCircle, ArrowRight } from 'lucide-react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAdConfigs } from '../../hooks/useAdDomains'
import { formatDateTime } from '../../utils/format'

const STATUS_CONFIG = {
  Online:  { labelKey: 'common.status.online',  cls: 'badge-success', Icon: CheckCircle2 },
  Offline: { labelKey: 'common.status.offline', cls: 'badge-gray',    Icon: WifiOff      },
  Pending: { labelKey: 'common.status.pending', cls: 'badge-warning', Icon: Clock        },
  Error:   { labelKey: 'common.status.error',   cls: 'badge-error',   Icon: AlertCircle  },
}

export default function DomainStatusSection() {
  const { t } = useTranslation()
  const { data: configs, isLoading } = useAdConfigs()

  return (
    <div className="card p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.domainStatus.title')}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{t('dashboard.domainStatus.subtitle')}</p>
        </div>
        <Link to="/domains" className="btn btn-secondary text-sm flex items-center gap-1">
          {t('dashboard.domainStatus.manage')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {isLoading ? (
        <div className="text-center py-6 text-gray-400 text-sm">{t('dashboard.domainStatus.loading')}</div>
      ) : !configs?.length ? (
        <div className="text-center py-8">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t('dashboard.domainStatus.empty')}</p>
          <Link to="/domains" className="btn btn-primary text-sm mt-3 inline-flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            {t('dashboard.domainStatus.addDomain')}
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {configs.map((d) => {
            const status = STATUS_CONFIG[d.connectionStatus] ?? STATUS_CONFIG.Pending
            const StatusIcon = status.Icon
            return (
              <div key={d.id} className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="icon-box flex-shrink-0 w-8 h-8" style={{ background: 'var(--theme-icon-box-background)', color: 'var(--theme-icon-box-text)' }}>
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.displayName}</p>
                    <p className="text-xs text-gray-500 font-mono truncate">{d.server}:{d.port}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {d.lastConnectionTest && (
                    <span className="text-xs text-gray-400 hidden sm:block">
                      {formatDateTime(d.lastConnectionTest, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  <span className={clsx('badge', status.cls, 'flex items-center gap-1 text-xs')}>
                    <StatusIcon className="w-3 h-3" />
                    {t(status.labelKey)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
